import classNames from 'classnames';
import { FusionCanvas } from '@rendering/fusion-react';
import { FlexBox, Button, Copy } from '@cimpress/react-components';
import { useState, useEffect } from 'react';
import { getTransientDocumentUrl } from '../../../../api';
import { uploadJsonDocumentAndGetDocumentUrl } from '../../../../api/uploadJsonDocumentAndGetDocumentUrl';
import { ErrorIndicator, LoadingIndicator, PanelSwitcher } from '../components';
import type { PreviewRendererProps } from '../types';
import { useLoadCardData } from '../useLoadCardData';
import classes from '../PreviewCard.module.css';
import { getStudioUrl } from '../../../../tools';
import { dimensionsToString } from './helpers';

const CANVAS_DIMENSION = { height: 300 };
// Larger render for full-width cards; CSS max-width scales it down to fit the card
const FILL_WIDTH_CANVAS_DIMENSION = { maxDimension: 600 };
const TEXT_OPTIONS = { rtextEnabled: true };

export const DocumentPreviewRenderer = ({ loader, fillWidth = false }: PreviewRendererProps<'document'>) => {
    const {
        data,
        isLoading,
        loadingLabel,
        hasError,
        errorMessage,
        panels,
        panelIndex,
        onPreviewLoaded,
        onPreviewError,
        onPanelIndexChange,
    } = useLoadCardData(loader);

    const handleFusionError = (error: unknown) => {
        if (error instanceof TypeError && error.message.includes('getContext')) {
            // Sometimes Fusion tries to render after its canvas is unmounted,
            // we don't want to show an error in that case
            return;
        }

        onPreviewError();
    };

    const document = data?.document;
    const [documentUrl, setDocumentUrl] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!document) {
            setDocumentUrl(undefined);
            return;
        }

        const generateDocumentUrl = async () => {
            try {
                const url = getTransientDocumentUrl(document);
                setDocumentUrl(url);
            } catch (error) {
                // If getTransientDocumentUrl fails due to max length of 7000,
                // fallback to uploadJsonDocumentAndGetDocumentUrl
                try {
                    const uploadedUrl = await uploadJsonDocumentAndGetDocumentUrl(document, undefined, 1);
                    setDocumentUrl(uploadedUrl);
                } catch (uploadError) {
                    console.error('Failed to upload document:', uploadError);
                    setDocumentUrl(undefined);
                }
            }
        };

        generateDocumentUrl();
    }, [document]);

    const panelId = panels[panelIndex]?.id;

    return (
        <>
            {isLoading && <LoadingIndicator label={loadingLabel} />}

            {hasError && <ErrorIndicator message={errorMessage} />}

            {!hasError && document && panelId && (
                <div className={classNames(classes.preview, { [classes.hidden]: isLoading })}>
                    <FusionCanvas
                        cimDoc={document}
                        dimension={fillWidth ? FILL_WIDTH_CANVAS_DIMENSION : CANVAS_DIMENSION}
                        textOptions={TEXT_OPTIONS}
                        selector={{ type: 'panel', id: panelId }}
                        onPaint={onPreviewLoaded}
                        onError={handleFusionError}
                        referrer=""
                    />
                    <span>{dimensionsToString(panels, panelIndex)}</span>
                </div>
            )}
            {documentUrl && (
                <FlexBox flexDirection="column" alignItems="center" justifyContent="center">
                    <FlexBox flexDirection="row" style={{ gap: '4px' }}>
                        <Button
                            href={getStudioUrl(documentUrl, panels[panelIndex]?.width, panels[panelIndex]?.height)}
                            target="_blank"
                            style={{ fontSize: '12px', padding: '4px 10px' }}
                        >
                            Open in Studio
                        </Button>
                        <Button style={{ padding: '4px 10px' }}>
                            <Copy
                                style={{ background: 'transparent', padding: '0', display: 'inline-flex' }}
                                value={documentUrl}
                            >
                                documentUrl
                            </Copy>
                        </Button>
                    </FlexBox>
                </FlexBox>
            )}
            <PanelSwitcher panelCount={panels.length} panelIndex={panelIndex} onChange={onPanelIndexChange} />
        </>
    );
};
